<template>
	<div
		id="app"
		:class="{ 'is-online': isOnline, 'is-offline': !isOnline, 'no-video-object-fit': !supportsObjectFitOnVideos }"
	>
		<router-view v-if="!loading" />
		<LoadingAnimation v-if="loading" class="app__loading-animation" />
	</div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { getModule as getStoreModule } from 'vuex-module-decorators';
import { Watch } from 'vue-property-decorator';
import Sentry from '@madebyheyday/sentry-util';
import LoadingAnimation from '@components/040-LoadingAnimation/LoadingAnimation.vue';
import { throwIfNotOfflineError } from '@app/types/errors/OfflineError';
import AppStoreModule from './store/modules/AppStoreModule';
import GlobalsStoreModule from './store/modules/GlobalsStoreModule';
import UserStoreModule from './store/modules/UserStoreModule';
import PresentationStoreModule from './store/modules/PresentationStoreModule';
import AppModule from './types/base/AppModule';

declare const cloudinary: any;
@Component({
	components: {
		LoadingAnimation
	}
})
export default class App extends Vue {
	private appStore: AppStoreModule = getStoreModule(AppStoreModule);

	private userStore: UserStoreModule = getStoreModule(UserStoreModule);

	private globalsStore: GlobalsStoreModule = getStoreModule(GlobalsStoreModule);

	private presentationStore: PresentationStoreModule = getStoreModule(PresentationStoreModule);

	protected loading: boolean = true;

	protected get supportsObjectFitOnVideos(): boolean {
		const isEdge: boolean = navigator.userAgent.indexOf('Edge/') >= 0;
		const testImg: HTMLImageElement = new Image();
		const supportsVideoObjectFit: boolean = 'object-fit' in testImg.style && !isEdge;

		return supportsVideoObjectFit;
	}

	protected get isOnline(): boolean {
		return this.appStore.isOnline;
	}

	private async initializeCache(): Promise<void> {
		if ('storage' in navigator && 'estimate' in navigator.storage) {
			const { usage, quota } = await navigator.storage.estimate();
			if (usage && quota) {
				const minimumRequiredTotalQuota: number = 8 * 1024 * 1024 * 1024;

				if (quota < minimumRequiredTotalQuota) {
					this.appStore.setSupportsCaching(false);
					Sentry.setExtra('clientSupportsCaching', false);
					console.log('[App] Caching disabled as per insufficient quota');
				} else {
					this.appStore.setSupportsCaching(true);

					console.log(
						`[App] Cache utilization: ${(Math.round((usage / quota) * 1000) / 10).toLocaleString('en')}% (${(
							Math.round((usage / 1024 / 1024 / 1024) * 10) / 10
						).toLocaleString('en')} GB / ${(Math.round((quota / 1024 / 1024 / 1024) * 10) / 10).toLocaleString(
							'en'
						)} GB total max.)`
					);

					Sentry.setExtras({
						cacheUsage: `${(Math.round((usage / 1024 / 1024 / 1024) * 10) / 10).toLocaleString('en')} GB (${(
							Math.round((usage / quota) * 1000) / 10
						).toLocaleString('en')}%)`,
						cacheQuota: `${(Math.round((quota / 1024 / 1024 / 1024) * 10) / 10).toLocaleString('en')} GB`,
						clientSupportsCaching: true
					});
				}
			}
		}
	}

	private setConnectionState(): void {
		this.appStore.setIsOnline(navigator.onLine);
		if (navigator.onLine) {
			console.log('[App] Internet connection found. App is running in online mode.');
		} else {
			console.log('[App] No internet connection found. App is running in offline mode.');
		}
	}

	private ensureExternalScriptsAreLoaded(): void {
		if (typeof cloudinary === 'undefined') {
			const mediaLibrarylScriptElement: HTMLScriptElement = document.createElement('script');
			mediaLibrarylScriptElement.src = 'https://media-library.cloudinary.com/global/all.js';
			mediaLibrarylScriptElement.async = true;
			document.body.appendChild(mediaLibrarylScriptElement);
		}

		if (!(window as any).showCollectorDialog) {
			const issueCollectorlScriptElement: HTMLScriptElement = document.createElement('script');
			issueCollectorlScriptElement.src =
				'https://jungfrau.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/-t2cu9l/b/11/a44af77267a987a660377e5c46e0fb64/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=de-DE&collectorId=6798a3a1';
			issueCollectorlScriptElement.async = true;
			document.body.appendChild(issueCollectorlScriptElement);
		}
	}

	protected async mounted(): Promise<void> {
		this.setConnectionState();
		this.initializeCache();

		window.addEventListener('online', this.setConnectionState.bind(this));
		window.addEventListener('offline', this.setConnectionState.bind(this));

		if (this.appStore.activeAppModule !== AppModule.Share) {
			// use what's already stored locally as a basis and load new data on top when online
			await Promise.all([this.userStore.restoreStateIfSupported(), this.globalsStore.restoreStateIfSupported()]);

			await this.presentationStore.restoreStateIfSupported();
		}

		if (this.appStore.activeAppModule === AppModule.Share) {
			await this.globalsStore.loadAll().catch(throwIfNotOfflineError);
		} else if (this.isOnline) {
			await this.globalsStore.loadAll().catch(throwIfNotOfflineError);

			this.globalsStore.saveStateIfSupported();

			this.ensureExternalScriptsAreLoaded();
		} else {
			console.log('[App] Restored store states from cache for offline use.');
		}

		this.loading = false;
	}

	/**
	 * Make sure globals are refreshed, external scripts are loaded and queued sentry errors are processed
	 * once the app comes back online
	 */
	@Watch('isOnline')
	protected async onOnlineStateChange(): Promise<void> {
		Sentry.addBreadcrumb({
			level: Sentry.Severity.Debug,
			message: `Online state changed to ${this.isOnline ? 'online' : 'offline'}`
		});

		if (this.isOnline) {
			await this.globalsStore.loadAll().catch(throwIfNotOfflineError);

			if (this.appStore.activeAppModule !== AppModule.Share) {
				this.globalsStore.saveStateIfSupported();
			}

			this.ensureExternalScriptsAreLoaded();
			this.appStore.processCapturedErrors();
		}
	}
}
</script>
